import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAdminSignupMutation } from '../../api/authApi';
import { SHARK_SPECIES } from '../../../admin/components/SharkAvatarSelector';

export function useSignupHook() {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'Admin' });
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [signup, { isLoading }] = useAdminSignupMutation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [shark, setShark] = useState({
    name: 'Finn',
    species: 'blue_shark',
    costume: 'default',
    color: 'transparent',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleNext = () => {
    if (!formData.name || !formData.email || !formData.password) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all fields',
        icon: 'warning',
        confirmButtonColor: '#00cfcf',
      });
      return;
    }

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && Date.now() >= payload.exp * 1000) {
          Swal.fire({
            title: 'Link Expired',
            text: 'This invitation link has expired. Please request a new invitation from your administrator.',
            icon: 'error',
            confirmButtonColor: '#00cfcf',
          });
          return;
        }
        if (payload.email && formData.email.toLowerCase() !== payload.email.toLowerCase()) {
          Swal.fire({
            title: 'Email Mismatch',
            text: 'Email does not match the invitation.',
            icon: 'error',
            confirmButtonColor: '#00cfcf',
          });
          return;
        }
      } catch (e) {
        console.error('Invalid token format', e);
      }
    }
    setStep(2);
  };

  const submitForm = async () => {
    if (!shark.name.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please give your shark a name before continuing.',
        icon: 'warning',
        confirmButtonColor: '#00cfcf',
      });
      return;
    }

    try {
      await signup({
        ...formData,
        plan: 'freemium',
        invite_token: token || undefined,
        sharkAvatar: shark.species || 'blue_shark',
        sharkName: shark.name.trim(),
        sharkCostume: shark.costume || 'default',
      }).unwrap();

      Swal.fire({
        title: 'Registration Complete',
        text: token
          ? 'You have successfully joined the workspace. Please log in to get started.'
          : 'Your account is ready. Log in to start your trial with 105 free credits.',
        icon: 'success',
        confirmButtonColor: '#38bdf8',
        confirmButtonText: 'Proceed to Login',
        background: '#0B1120',
        color: '#ffffff',
      });
      navigate('/login');
    } catch (err: any) {
      Swal.fire({
        title: 'Registration Failed',
        text: err?.data?.message || err?.message || 'Something went wrong.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
        background: '#0B1120',
        color: '#ffffff',
      });
    }
  };

  const handleSharkSpeciesChange = (species: string) => {
    const newShark = SHARK_SPECIES.find((s) => s.id === species);
    setShark((prev) => ({
      ...prev,
      species,
      name: SHARK_SPECIES.some((s) => s.defaultName === prev.name)
        ? newShark?.defaultName || prev.name
        : prev.name,
    }));
  };

  const handleSharkCostumeChange = (costume: string) => {
    setShark((prev) => ({ ...prev, costume }));
  };

  const handleSharkNameChange = (name: string) => {
    setShark((prev) => ({ ...prev, name }));
  };
  
  const handleSharkColorChange = (hex: string) => {
    setShark((prev) => ({ ...prev, color: hex }));
  };

  return {
    formData,
    step,
    showPassword,
    isLoading,
    shark,
    setStep,
    setShowPassword,
    handleChange,
    handleNext,
    submitForm,
    handleSharkSpeciesChange,
    handleSharkCostumeChange,
    handleSharkNameChange,
    handleSharkColorChange
  };
}
